<template>
	<div class="border border-border-primary">
		<Click
			v-if="index === 2"
			:placement="AdPosition.ACTION_OFFER_LEAFLET_SECOND"
			class="max-sm:hidden lg:hidden my-2"
		/>
		<Click
			v-if="index === 2"
			:placement="AdPosition.ACTION_OFFER_LEAFLET_FIRST"
			class="sm:hidden my-2"
		/>
		<template v-if="interactive">
			<LeafletZoomWrapper ref="zoomWrapper">
				<ImageOverlay
					:images="images"
					:metadata="metadata"
					:width="1540"
					:height="2000"
					:alt="`Akční leták ${actionOfferName ?? ''}`"
					class="!block"
					:on-item-click="handleItemClick"
					:can-close-overlay-tooltip="handleCanCloseOverlayTooltip"
					@image-loaded="emit('image-loaded')"
					@metadata-loaded="emit('metadata-loaded', true)"
					@metadata-error="emit('metadata-loaded', false)"
				>
					<template #tooltip="{item}">
						<div v-if="item.productInfo" class="h-full w-full flex flex-col justify-end cursor-pointer">
							<div class="py-2 px-3 bg-white/80 backdrop-blur-xs w-full relative rounded-b-lg">
								<div class="typo-body-s text-body-highlight font-bold underline mb-2">
									{{ item.productInfo?.Name }}
								</div>
								<LeafletPricing
									v-if="item.productInfo"
									:product-info="item.productInfo"
									class="mb-1"
								/>
								<LeafletProductPriceValidity
									:product-info="item.productInfo"
									:valid-from="validFrom"
									:valid-to="validTo"
								/>
								<div
									v-if="item.productInfo"
									:class="[
										LEAFLET_ZOOM_EXCLUDE_CLASS,
										'absolute -right-3 -bottom-3 z-10 pointer-events-auto rounded-full',
										isAuthenticated && 'bg-background-secondary p-0.5 shadow-green-sm',
									]"
									@click.stop
									@mousedown.stop
								>
									<LeafletShoppingListControl
										v-model:is-shopping-list-open="isShoppingListPickerOpen"
										:product-info="item.productInfo"
									/>
								</div>
							</div>
						</div>
					</template>
				</ImageOverlay>
			</LeafletZoomWrapper>
			<LeafletProductModal
				v-if="isCurrentProductModalPage"
				v-model:is-open="isOpen"
				:product-info="selectedProductInfo"
				:price-validity-text="selectedPriceValidityText"
				:hide-price-validity="isComingLeaflet"
			>
				<template #navigation>
					<button
						type="button"
						class="pointer-events-auto flex-none rounded-full bg-white text-icon-primary hover:bg-background-noticeable disabled:text-body-disabled p-3 sm:p-2 shadow-gray-md disabled:shadow-sm max-lg:rounded-l-none"
						:disabled="!canGoPrev"
						aria-label="Předchozí produkt"
						@click="goPrevious()"
					>
						<NuxtIcon name="chevron-left" filled class="text-2xl md:text-3xl" />
					</button>
					<button
						type="button"
						class="pointer-events-auto flex-none rounded-full bg-white text-icon-primary hover:bg-background-noticeable disabled:text-body-disabled p-3 sm:p-2 shadow-gray-md disabled:shadow-sm max-lg:rounded-r-none"
						:disabled="!canGoNext"
						aria-label="Další produkt"
						@click="goNext()"
					>
						<NuxtIcon name="chevron-right" filled class="text-2xl md:text-3xl" />
					</button>
				</template>
			</LeafletProductModal>
		</template>
		<template v-else>
			<NuxtImg
				v-if="availableProcessedImages"
				#default="{imgAttrs}"
				width="1218"
				height="1624"
				:src="images.original || ''"
				class="mx-auto border border-old-light-300"
				:custom="true"
				@load="emit('image-loaded')"
				@error="emit('image-loaded')"
			>
				<img
					v-bind="imgAttrs"
					:srcset="createSrcset(images)"
				/>
			</NuxtImg>
			<NuxtImg
				v-else
				:src="images.original"
				:alt="`Akční leták ${actionOfferName ?? ''}`"
				width="1218"
				height="1632"
				densities="x1"
				class="mx-auto border border-border-tertiary"
				@load="emit('image-loaded')"
				@error="emit('image-loaded')"
			/>
		</template>
	</div>
</template>

<script setup lang="ts">
	import {ImageOverlay, type ImageSet, type OverlayItem} from '@globus/image-overlay';
	import {AdPosition} from '~/openapi';
	import {leafletProductNavigationKey} from '~/composables/useLeafletProductNavigation';
	import {LEAFLET_ZOOM_EXCLUDE_CLASS} from '#shared/constants';
	import {getProductPriceValidityText, isLeafletComing} from '~/utils/leaflets';
	import '@globus/image-overlay/style.css';

	type Props = {
		index: number;
		images: ImageSet;
		metadata: {original: string};
		actionOfferName?: string | null;
		validFrom?: string | null;
		validTo?: string | null;
		interactive?: boolean;
		availableProcessedImages?: boolean;
	};

	type Emits = {
		'image-loaded': [];
		'metadata-loaded': [loaded?: boolean];
	};

	const props = withDefaults(defineProps<Props>(), {
		interactive: true,
		availableProcessedImages: false,
	});
	const emit = defineEmits<Emits>();

	const navigation = inject(leafletProductNavigationKey, null);
	const {isAuthenticated} = useAuth();
	const fallbackOpen = ref(false);
	const isShoppingListPickerOpen = ref(false);
	const zoomWrapper = useTemplateRef('zoomWrapper');

	const isOpen = computed({
		get: () => navigation?.isOpen.value ?? fallbackOpen.value,
		set: (value) => {
			if (navigation) {
				navigation.isOpen.value = value;
			} else {
				fallbackOpen.value = value;
			}
		},
	});
	const selectedProductInfo = computed(() => navigation?.selectedProductInfo.value ?? null);
	const currentPageIndex = computed(() => navigation?.currentPageIndex.value ?? 0);
	const canGoNext = computed(() => navigation?.canGoNext.value ?? false);
	const canGoPrev = computed(() => navigation?.canGoPrev.value ?? false);

	const isCurrentProductModalPage = computed(() => currentPageIndex.value === props.index);

	watch(isOpen, (open) => {
		if (open) {
			zoomWrapper.value?.reset();
			return;
		}

		nextTick(() => {
			zoomWrapper.value?.reset();
		});
	});

	const isComingLeaflet = computed(() => isLeafletComing({
		validFrom: props.validFrom,
		validTo: props.validTo,
	}));

	const selectedPriceValidityText = computed(() => getProductPriceValidityText(
		selectedProductInfo.value,
		props.validFrom,
		props.validTo,
	));

	function createSrcset(image: ImageSet): string {
		return Object.entries(image)
			.map(([key, value]) => key === 'original' ? `${value} 1865w` : `${value} ${key}w`)
			.join(', ');
	}

	function handleItemClick(item: OverlayItem): boolean {
		if (!item.productInfo || !navigation) {
			return false;
		}

		isShoppingListPickerOpen.value = false;
		zoomWrapper.value?.reset();
		navigation.open(props.index, item.productInfo);

		return false;
	}

	function handleCanCloseOverlayTooltip(): boolean | void {
		if (isShoppingListPickerOpen.value) {
			return false;
		}
	}

	function goNext() {
		navigation?.goNext();
	}

	function goPrevious() {
		navigation?.goPrevious();
	}
</script>
